"use client";

import { cn } from "@/lib/utils";
import { IBlocks, IThroneWishlist, IUserData } from "@/types";
import React from "react";
import type { BlockUpdateFunction } from "./utils/blocks-map";
import AdminPopover from "./utils/AdminPopover";
import { SHADOW_OPTIONS } from "./utils/block-style-classes";
interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
}
export default function BlockThroneWishlist({
    edit,
    account,
    block,
    update,
    style,
    theme,
}: Props) {
    let storeButtonStyle = {
        ...style,
        background: style.background,
        color: style.color,
    };
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [items, setItems] = React.useState<IThroneWishlist["items"]>([]);
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await ThroneWishlist(
                    blockUpdate.options.username,
                    blockUpdate.options.user_id
                );
                setItems(data.wishlist?.items || []);
            } catch (error) {
                console.error(error);
                setItems([]);
            }
        };
        fetchData();
    }, [account, blockUpdate.options.user_id]);
    const getGridCols = (itemCount: number) => {
        switch (itemCount) {
            case 1:
                return "grid-cols-3 [&>*:first-child]:col-span-3";
            case 2:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:last-child]:col-span-1";
            case 3:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*]:col-span-1 [&>*:last-child]:col-span-full";
            case 4:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:last-child]:col-span-2";
            case 5:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-1 [&>*:last-child]:col-span-1";
            case 6:
                return "grid-cols-3 [&>*:first-child]:col-span-2 [&>*:nth-child(2)]:col-span-1 [&>*:nth-child(3)]:col-span-1 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:last-child]:col-span-1";
            case 7:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2";
            case 8:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(4)]:col-span-2 [&>*:nth-child(5)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 9:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 [&>*:nth-child(8)]:col-span-2";
            case 10:
                return "grid-cols-3 [&>*]:col-span-1 [&>*:first-child]:col-span-2 [&>*:nth-child(7)]:col-span-2 ";
            default:
                return "grid-cols-3 [&>*:nth-child(3n+1)]:col-span-1 [&>*:nth-child(3n+2)]:col-span-1 [&>*:nth-child(3n)]:col-span-1";
        }
    };
    const productBackground = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_background
        : theme.profile.colors.primary;
    const productText = blockUpdate.options?.custom_theme
        ? blockUpdate.options?.product_text
        : theme.profile.colors.primary_text;
    if (blockUpdate.options?.custom_theme) {
        storeButtonStyle.background =
            blockUpdate.options?.store_button_background;
        storeButtonStyle.color = blockUpdate.options?.store_button_text;
    }
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={"Thone Wishlist"}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div
                key={block.id}
                className={cn(
                    `relative flex w-full flex-row flex-wrap gap-2 rounded-user p-2`,
                    "block-" + block.id
                )}
                id="ThoneWishlist"
            >
                <div
                    className={cn(
                        "grid w-full grid-cols-1 gap-1",
                        getGridCols(items.length)
                    )}
                >
                    {items.map((item) => (
                        <a
                            href={`${item.link}?utm_source=doras-profile&utm_medium=profile-click`}
                            target="_blank"
                            // rel=""
                            key={item.id}
                            className={cn(
                                "relative flex scale-95 flex-col items-center gap-0 rounded-user transition-transform duration-300 hover:scale-100"
                            )}
                        >
                            <img
                                src={item.imgLink}
                                alt={item.name}
                                className={cn(
                                    "h-64 w-full overflow-hidden rounded-user object-cover"
                                )}
                            />
                            <div
                                className={cn(
                                    "absolute inset-x-0 bottom-0 flex w-full flex-col items-start gap-2 rounded-user px-1",
                                    "rounded-t-none"
                                )}
                                style={{
                                    background: productBackground,
                                    color: productText,
                                }}
                            >
                                <h3 className="w-full truncate text-ellipsis text-lg font-semibold">
                                    {item.name}
                                </h3>
                            </div>
                        </a>
                    ))}
                </div>
                <a
                    className={`w-full`}
                    href={"https://throne.com/" + blockUpdate.options.username}
                    target="_blank"
                >
                    <button
                        id="store-button"
                        className={cn(
                            "rounded-user bg-user-primary-color text-user-primary-text-color",
                            SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                            "w-full px-1 py-3 text-xl transition-all duration-150 hover:scale-105",
                            blockUpdate.name.replaceAll(" ", "-"),
                            "block-" + block.id + "-store-button"
                        )}
                        style={storeButtonStyle}
                    >
                        <span
                            id="block-text"
                            className={`block w-full px-6 text-center font-bold`}
                        >
                            See More
                        </span>
                    </button>
                </a>
            </div>
        </AdminPopover>
    );
}

async function ThroneWishlist(
    username: string,
    user_id: string
): Promise<{
    wishlist: IThroneWishlist | null;
}> {
    const wishlistResponse: IThroneWishlist = await fetch(
        "https://us-central1-onlywish-9d17b.cloudfunctions.net/api-extensionv2/v1/twitch/wishlist/" +
            user_id,
        {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        }
    ).then(async (response) => await response.json());
    const items = wishlistResponse.items.map((item) => {
        item.link = `https://throne.com/${username}/item/${item.id}`;
        return item;
    });
    return {
        wishlist: {
            collections: wishlistResponse.collections,
            featuredItem: wishlistResponse.featuredItem && {
                ...wishlistResponse.featuredItem,
                link: `https://throne.com/${username}/item/${wishlistResponse.featuredItem.id}`,
            },
            items,
        },
    };
}
